import React from 'react';
import { Box, Typography, Grid, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { formatAddressFromFields, formatCurrency } from './utils';

const BOLPrintable = React.forwardRef(({ 
  bol, 
  purchaseOrder,
  company, 
  lineItems,
  showSummary = true,
  showLotBreakdown = true
}, ref) => {
  // Format date without timezone issues
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    // If it's already a formatted date string, return it
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    // Parse date string (YYYY-MM-DD) directly without timezone conversion
    if (typeof dateString === 'string' && dateString.includes('-')) {
      const parts = dateString.split('-');
      if (parts.length === 3) {
        const year = parts[0];
        const month = parts[1];
        const day = parts[2];
        return `${month}/${day}/${year}`;
      }
    }
    // Fallback: use Date but add noon to avoid timezone issues
    const date = new Date(dateString + 'T12:00:00');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  // Convert country code to full name
  const getCountryFullName = (country) => {
    if (!country) return '';
    const countryMap = {
      'US': 'United States',
      'USA': 'United States',
      'United States': 'United States',
      'CA': 'Canada',
      'Canada': 'Canada',
      'MX': 'Mexico',
      'Mexico': 'Mexico',
      'UK': 'United Kingdom',
      'GB': 'United Kingdom',
      'United Kingdom': 'United Kingdom'
    };
    return countryMap[country] || country;
  };

  // Format company address properly
  const getFormattedCompanyAddress = () => {
    if (!company) return '';
    
    let address = '';
    
    if (company.address) address += company.address;
    
    if (company.city || company.state || company.zip_code) {
      address += address ? '\n' : '';
      if (company.city) address += company.city;
      if (company.state) {
        if (company.city) address += ', ';
        address += company.state;
      }
      if (company.zip_code) {
        if (company.city || company.state) address += ' ';
        address += company.zip_code;
      }
    }
    
    if (company.country) {
      address += address ? '\n' : '';
      address += getCountryFullName(company.country);
    }
    
    return address;
  };

  return (
    <div ref={ref} style={{ padding: '24px', backgroundColor: '#fff' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4, borderBottom: '2px solid #000', pb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1, fontWeight: 400 }}>BILL OF LADING</Typography>
          <Typography variant="body2" color="text.secondary">
            BOL #: {bol.bol_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Proforma Invoice #: {purchaseOrder?.po_number || 'N/A'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Date: {formatDate(new Date().toISOString().split('T')[0])}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h6" sx={{ fontWeight: 400 }}>
            {company?.name || 'Your Company Name'}
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {getFormattedCompanyAddress() || 'Address not provided'}
          </Typography>
          {company?.phone && (
            <Typography variant="body2">
              Phone: {company.phone}
            </Typography>
          )}
          {company?.email && (
            <Typography variant="body2">
              Email: {company.email}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Shipping Information */}
      {(bol.carrier_name || bol.tracking_number) && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Shipping Information
          </Typography>
          <Grid container spacing={2}>
            {bol.carrier_name && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Carrier:</strong> {bol.carrier_name}
                </Typography>
              </Grid>
            )}
            {bol.tracking_number && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Tracking #:</strong> {bol.tracking_number}
                </Typography>
              </Grid>
            )}
            {bol.ship_date && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Ship Date:</strong> {formatDate(bol.ship_date)}
                </Typography>
              </Grid>
            )}
            {bol.expected_delivery_date && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Expected Delivery:</strong> {formatDate(bol.expected_delivery_date)}
                </Typography>
              </Grid>
            )}
            {bol.delivery_status && (
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Status:</strong> {bol.delivery_status.replace(/_/g, ' ').toUpperCase()}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Origin and Destination */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', borderBottom: '1px solid #000' }}>
            Shipper (From):
          </Typography>
          <Typography variant="body2">
            {bol.shipper_name || 'Not specified'}
          </Typography>
          {bol.shipper_contact && (
            <Typography variant="body2">
              Phone: {bol.shipper_contact}
            </Typography>
          )}
          {bol.shipper_email && (
            <Typography variant="body2">
              Email: {bol.shipper_email}
            </Typography>
          )}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 1 }}>
            {formatAddressFromFields(
              bol.origin_street,
              bol.origin_city,
              bol.origin_state,
              bol.origin_zip,
              bol.origin_country
            ) || 'No address provided'}
          </Typography>
          {bol.pickup_location && (
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Pickup: {bol.pickup_location}
            </Typography>
          )}
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', borderBottom: '1px solid #000' }}>
            Consignee (To):
          </Typography>
          <Typography variant="body2">
            {bol.consignee_name || 'Not specified'}
          </Typography>
          {bol.consignee_company && (
            <Typography variant="body2">
              Company: {bol.consignee_company}
            </Typography>
          )}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mt: 1 }}>
            {formatAddressFromFields(
              bol.destination_street,
              bol.destination_city,
              bol.destination_state,
              bol.destination_zip,
              bol.destination_country
            ) || 'No address provided'}
          </Typography>
          {bol.delivery_location && (
            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
              Delivery: {bol.delivery_location}
            </Typography>
          )}
        </Grid>
      </Grid>

      {/* Cargo Details */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold', borderBottom: '1px solid #000' }}>
          Cargo Description
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-line', mb: 2 }}>
          {bol.goods_description || 'See line items below'}
        </Typography>
        
        <Grid container spacing={2}>
          {(bol.container_number || bol.seal_number) && (
            <>
              {bol.container_number && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Container #:</strong> {bol.container_number}
                  </Typography>
                </Grid>
              )}
              {bol.seal_number && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2">
                    <strong>Seal #:</strong> {bol.seal_number}
                  </Typography>
                </Grid>
              )}
            </>
          )}
          {(bol.num_pallets || bol.num_cases || bol.num_units) ? (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Quantity:</strong>{' '}
                {bol.num_pallets ? `${bol.num_pallets} Pallet(s) ` : ''}
                {bol.num_cases ? `${bol.num_cases} Case(s) ` : ''}
                {bol.num_units ? `${bol.num_units} Unit(s)` : ''}
              </Typography>
            </Grid>
          ) : null}
          {(bol.container_length || bol.container_width || bol.container_height) && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Dimensions:</strong>{' '}
                {bol.container_length && `${bol.container_length}`}
                {bol.container_width && ` x ${bol.container_width}`}
                {bol.container_height && ` x ${bol.container_height}`}
                {bol.dimension_unit && ` ${bol.dimension_unit}`}
              </Typography>
            </Grid>
          )}
          {bol.total_weight && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Weight:</strong> {bol.total_weight} {bol.weight_unit || 'lbs'}
              </Typography>
            </Grid>
          )}
          {bol.hazardous_materials && (
            <Grid item xs={12}>
              <Box sx={{ 
                border: '1px solid #d32f2f', 
                borderRadius: 1, 
                p: 1.5, 
                mt: 1,
                bgcolor: '#ffebee'
              }}>
                <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 'bold', mb: 1 }}>
                  HAZARDOUS MATERIALS
                </Typography>
                {bol.hazmat_details && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {bol.hazmat_details}
                  </Typography>
                )}
                {bol.un_number && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>UN Number:</strong> {bol.un_number}
                  </Typography>
                )}
                {bol.hazard_class && (
                  <Typography variant="body2">
                    <strong>Hazard Class:</strong> {bol.hazard_class}
                  </Typography>
                )}
              </Box>
            </Grid>
          )}
          {bol.special_handling && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <strong>Special Handling:</strong> {bol.special_handling}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Summary Table - Aggregated by Product */}
      {lineItems && lineItems.length > 0 && showSummary && (
        <>
          <Box sx={{ mb: 2, mt: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              Order Summary
            </Typography>
          </Box>
          <TableContainer sx={{ 
            overflowX: 'auto',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            mb: 4
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Size</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>UoM</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const summaryMap = new Map();

                  lineItems.forEach(item => {
                    if (!item.description) return;

                    const key = `${item.product_id || 'no-product'}_${item.variant_id || 'no-variant'}_${item.unit_type || 'Case'}`;

                    if (!summaryMap.has(key)) {
                      summaryMap.set(key, {
                        product_name: item.product_name || item.description?.split(' - ')[0] || 'N/A',
                        variant_name: item.variant_name || null,
                        unit_type: item.unit_type || 'Case',
                        units_per_case: item.units_per_case || null,
                        quantity: 0
                      });
                    }

                    const summary = summaryMap.get(key);
                    summary.quantity += parseFloat(item.quantity) || 0;
                  });

                  return Array.from(summaryMap.values()).map((summary, idx) => {
                    const isCase = summary.unit_type === 'Case';
                    const qtyLabel = isCase
                      ? `${summary.quantity} ${summary.quantity === 1 ? 'Case' : 'Cases'}`
                      : `${summary.quantity}`;
                    return (
                      <TableRow key={idx}>
                        <TableCell>
                          <Typography variant="body2">{summary.product_name}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{summary.variant_name || '-'}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">{summary.units_per_case ? `${summary.units_per_case}U/C` : '-'}</Typography>
                        </TableCell>
                        <TableCell align="center">{qtyLabel}</TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Lot Breakdown Table */}
      {lineItems && lineItems.length > 0 && showLotBreakdown && (
        <>
          <Box sx={{ mb: 2, mt: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, pb: 1, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
              Lot Breakdown
            </Typography>
          </Box>
          <TableContainer sx={{ 
            overflowX: 'auto',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            mb: 3
          }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Item</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Size</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>UoM</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>Lot #</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lineItems.map((item, idx) => {
                  const isCase = (item.unit_type || 'Case') === 'Case';
                  const qty = parseFloat(item.quantity) || 0;
                  const qtyLabel = isCase ? `${qty} ${qty === 1 ? 'Case' : 'Cases'}` : `${qty}`;
                  return (
                    <TableRow key={idx}>
                      <TableCell>
                        <Typography variant="body2">
                          {item.product_name || item.description?.split(' - ')[0] || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{item.variant_name || '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2">{item.units_per_case ? `${item.units_per_case}U/C` : '-'}</Typography>
                      </TableCell>
                      <TableCell align="center">{qtyLabel}</TableCell>
                      <TableCell align="center">{item.lot_number || '-'}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Payment/Documentation */}
      {(bol.freight_terms || bol.insurance_required) && (
        <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Payment & Documentation
          </Typography>
          <Grid container spacing={2}>
            {bol.freight_terms && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Freight Terms:</strong> {bol.freight_terms}
                </Typography>
              </Grid>
            )}
            {bol.payment_responsibility && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Payment By:</strong> {bol.payment_responsibility}
                </Typography>
              </Grid>
            )}
            {bol.freight_charges && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>Freight Charges:</strong> {formatCurrency(bol.freight_charges)}
                </Typography>
              </Grid>
            )}
            {bol.cod_amount && (
              <Grid item xs={6}>
                <Typography variant="body2">
                  <strong>COD Amount:</strong> {formatCurrency(bol.cod_amount)}
                </Typography>
              </Grid>
            )}
            {bol.insurance_required && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Insurance:
                  </Typography>
                </Grid>
                {bol.insurance_provider && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Provider: {bol.insurance_provider}
                    </Typography>
                  </Grid>
                )}
                {bol.insurance_policy_number && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Policy: {bol.insurance_policy_number}
                    </Typography>
                  </Grid>
                )}
                {bol.insurance_coverage && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Coverage: {formatCurrency(bol.insurance_coverage)}
                    </Typography>
                  </Grid>
                )}
                {bol.insurance_contact && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Contact: {bol.insurance_contact}
                    </Typography>
                  </Grid>
                )}
                {bol.insurance_details && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      Details: {bol.insurance_details}
                    </Typography>
                  </Grid>
                )}
              </>
            )}
            {(bol.reference_1 || bol.reference_2) && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Reference Numbers:
                  </Typography>
                </Grid>
                {bol.reference_1 && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Ref #1: {bol.reference_1}
                    </Typography>
                  </Grid>
                )}
                {bol.reference_2 && (
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Ref #2: {bol.reference_2}
                    </Typography>
                  </Grid>
                )}
              </>
            )}
            {bol.documentation_notes && (
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  Additional Notes:
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                  {bol.documentation_notes}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {/* Terms and Conditions */}
      {bol.terms_conditions && (
        <Box sx={{ mb: 3, p: 2, border: '1px solid #000' }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Terms and Conditions
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', fontSize: '0.75rem' }}>
            {bol.terms_conditions}
          </Typography>
        </Box>
      )}

      {/* Signatures */}
      <Box sx={{ mt: 4, pageBreakInside: 'avoid' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ width: '45%', borderTop: '1px solid #000', pt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Shipper Signature
            </Typography>
          {bol.shipper_signature_name ? (
            <>
              <Typography variant="body2" sx={{ fontFamily: "'Brush Script MT', cursive", fontSize: '18pt', my: 1 }}>
                {bol.shipper_signature_name}
              </Typography>
              {bol.shipper_signature_title && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Title: {bol.shipper_signature_title}
                </Typography>
              )}
              {bol.shipper_signature_date && (
                <Typography variant="caption" sx={{ display: 'block' }}>
                  Date: {formatDate(bol.shipper_signature_date)}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Box sx={{ height: '40px' }} />
              <Typography variant="caption" sx={{ display: 'block' }}>
                Title: _______________________
              </Typography>
              <Typography variant="caption" sx={{ display: 'block' }}>
                Date: _______________________
              </Typography>
            </>
          )}
          </Box>
          <Box sx={{ width: '45%', borderTop: '1px solid #000', pt: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Carrier/Driver Signature
            </Typography>
            <Box sx={{ height: '40px' }} />
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              Driver Name: _______________________
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Date: _______________________
            </Typography>
          </Box>
        </Box>

        <Box sx={{ borderTop: '1px solid #000', pt: 1 }}>
          <Box sx={{ width: '45%' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              Consignee Signature (Delivery Receipt)
            </Typography>
            <Box sx={{ height: '40px' }} />
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              Received By: _______________________
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
              Date: _______________________
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Condition of Goods: _______________________
            </Typography>
          </Box>
        </Box>
      </Box>
    </div>
  );
});

export default BOLPrintable;